import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Pencil, Archive } from "lucide-react";
import { api, fdate, downloadPdf, errMsg } from "@/lib/api";
import { PageHeader, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function InterventionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [iv, setIv] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/interventions/${id}`).then((r) => setIv(r.data)).catch((e) => setError(errMsg(e)));
  }, [id]);

  if (error) return <p className="rounded-md bg-red-50 p-4 text-sm text-red-700" data-testid="iv-error">{error}</p>;
  if (!iv) return <p className="text-sm text-slate-500">Chargement…</p>;
  const r = iv.report || {};

  return (
    <div>
      <PageHeader kicker={`Intervention ${iv.number}`} title={iv.title}>
        <Button variant="outline" data-testid="iv-report-pdf-btn" onClick={() => downloadPdf(`/interventions/${id}/report.pdf`, `Rapport-${iv.number}.pdf`)}>
          <Download className="mr-1 h-4 w-4" /> Rapport PDF
        </Button>
        {!iv.archived && <Button className="bg-red-600 hover:bg-red-700" data-testid="iv-edit-btn" onClick={() => navigate(`/interventions/${id}/modifier`)}><Pencil className="mr-1 h-4 w-4" /> Mettre à jour</Button>}
      </PageHeader>
      {iv.archived && (
        <div className="mb-6 flex items-center gap-3 rounded-md border border-slate-300 bg-slate-900 px-5 py-3 text-sm text-white" data-testid="iv-archived-banner">
          <Archive className="h-4 w-4 text-red-400" /> Bilan archivé le {fdate(iv.archived_at)} — consultable par la direction.
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Panel title="Fiche" action={<StatusBadge status={iv.status} testId="iv-status-badge" />}>
          <dl className="space-y-3 text-sm">
            {[["Client", iv.client_name], ["Site", iv.site], ["Pôle", r.service_name], ["Période", `${fdate(iv.start_date)} → ${iv.end_date ? fdate(iv.end_date) : "en cours"}`],
              ["Heures", `${iv.hours} h`], ["Avancement", `${iv.progress} %`], ["Équipe", (iv.team || []).join(", ") || "—"], ["Saisi par", iv.created_by]].map(([k, v]) => (
              <div key={k}><dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt><dd className="mt-0.5 font-medium">{v}</dd></div>
            ))}
          </dl>
        </Panel>
        <article className="rounded-md border border-slate-200 bg-white" data-testid="iv-report">
          <div className="border-b-4 border-red-600 px-8 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Rapport d'intervention / Bilan de projet</p>
            <p className="mt-1 text-xs text-slate-500">Généré automatiquement le {r.generated_at ? new Date(r.generated_at).toLocaleString("fr-FR") : "—"}</p>
          </div>
          <div className="space-y-6 px-8 py-6 text-sm leading-relaxed text-slate-700">
            <section><h3 className="mb-1 font-display text-base font-semibold text-red-600">Synthèse</h3><p data-testid="iv-report-summary">{r.summary}</p></section>
            {(r.sections || []).map((s) => (
              <section key={s.title}><h3 className="mb-1 font-display text-base font-semibold text-red-600">{s.title}</h3><p className="whitespace-pre-line">{s.content}</p></section>
            ))}
            <section><h3 className="mb-1 font-display text-base font-semibold text-red-600">Conclusion</h3><p>{r.conclusion}</p></section>
          </div>
        </article>
      </div>
    </div>
  );
}
